import { useState } from 'react';

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  cover_image: string;
  media_files: Array<{ url: string; type: 'image' | 'video'; name: string }> | null;
  file_url: string;
  product_type: string;
  stock_unlimited: boolean;
  stock_quantity: number;
  shipping_info: string;
  weight: number;
}

interface StoreProfile {
  store_name: string;
  store_description: string;
  banner_image: string;
  logo_image: string;
  social_links?: Array<{ id: string; platform: string; label: string; url: string; is_active: boolean }>;
}

interface Profile {
  full_name: string;
  avatar_url: string;
}

interface Props {
  storeProfile: StoreProfile | null;
  userProfile: Profile | null;
  products: Product[];
  storeName: string;
  storeDesc: string;
  onBuyNow: (product: Product) => void;
  onCardClick: (product: Product) => void;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  activeFilter: 'all' | 'digital' | 'physical';
  setActiveFilter: (v: 'all' | 'digital' | 'physical') => void;
  filteredProducts: Product[];
}

function isVideoUrl(url: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase().split('?')[0];
  return lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm') || lower.endsWith('.ogg');
}

const categoryIcons: Record<string, string> = {
  'e-book': 'ri-book-line', 'template': 'ri-file-text-line', 'course': 'ri-graduation-cap-line',
  'audio': 'ri-music-line', 'video': 'ri-video-line', 'software': 'ri-code-box-line',
  'clothing': 'ri-t-shirt-line', 'accessories': 'ri-handbag-line', 'art': 'ri-paint-brush-line',
  'electronics': 'ri-cpu-line', 'books': 'ri-book-open-line', 'other': 'ri-folder-line',
};

const LINK_PLATFORMS: Record<string, { icon: string; color: string }> = {
  website:   { icon: 'ri-global-line',           color: '#9ca3af' },
  instagram: { icon: 'ri-instagram-line',         color: '#f472b6' },
  twitter:   { icon: 'ri-twitter-x-line',         color: '#f9fafb' },
  facebook:  { icon: 'ri-facebook-circle-line',   color: '#60a5fa' },
  tiktok:    { icon: 'ri-tiktok-line',            color: '#f9fafb' },
  youtube:   { icon: 'ri-youtube-line',           color: '#f87171' },
  linkedin:  { icon: 'ri-linkedin-box-line',      color: '#93c5fd' },
  whatsapp:  { icon: 'ri-whatsapp-line',          color: '#4ade80' },
  telegram:  { icon: 'ri-telegram-line',          color: '#7dd3fc' },
  custom:    { icon: 'ri-links-line',             color: '#4ade80' },
};

export default function DarkTemplate({ storeProfile, userProfile, products, storeName, storeDesc, onBuyNow, onCardClick, searchQuery, setSearchQuery, activeFilter, setActiveFilter, filteredProducts }: Props) {
  const digitalCount = products.filter(p => p.product_type !== 'physical').length;
  const physicalCount = products.filter(p => p.product_type === 'physical').length;
  const activeLinks = (storeProfile?.social_links || []).filter(l => l.is_active);

  return (
    <div className="min-h-screen font-sans" style={{ background: '#0f0f0f' }}>
      {/* Banner */}
      <div className="relative w-full h-72 overflow-hidden">
        {storeProfile?.banner_image ? (
          <img src={storeProfile.banner_image} alt="banner" className="w-full h-full object-cover object-top" />
        ) : (
          <img
            src="https://public.readdy.ai/ai/img_res/7a8ee669882918ae461cc5bd352c92ae.jpg"
            alt="banner"
            className="w-full h-full object-cover object-top"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-[#0f0f0f]"></div>
      </div>

      {/* Store Header */}
      <div className="max-w-7xl mx-auto px-6 -mt-16 relative z-10">
        <div className="flex items-end gap-5 mb-8">
          <div className="w-24 h-24 rounded-2xl overflow-hidden border-4 flex-shrink-0" style={{ borderColor: '#1a1a1a' }}>
            <img
              src={storeProfile?.logo_image || userProfile?.avatar_url || 'https://readdy.ai/api/search-image?query=minimal%20dark%20store%20logo%20icon%20on%20black%20background&width=200&height=200&seq=dark-logo-01&orientation=squarish'}
              alt="logo"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="pb-2">
            <h1 className="text-2xl font-extrabold text-white tracking-tight">{storeName}</h1>
            <p className="text-sm mt-1" style={{ color: '#9ca3af' }}>{storeDesc}</p>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: '#1a1a1a', color: '#4ade80' }}>
                <i className="ri-store-2-line mr-1"></i>{products.length} Products
              </span>
              {digitalCount > 0 && (
                <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: '#052e16', color: '#4ade80' }}>
                  <i className="ri-download-cloud-line mr-1"></i>{digitalCount} Digital
                </span>
              )}
              {physicalCount > 0 && (
                <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: '#431407', color: '#fb923c' }}>
                  <i className="ri-box-3-line mr-1"></i>{physicalCount} Physical
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Search + Filter */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#6b7280' }}></i>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', color: '#f9fafb' }}
            />
          </div>
          <div className="flex items-center gap-2">
            {(['all', 'digital', 'physical'] as const).map(f => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className="px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                style={activeFilter === f
                  ? { background: '#4ade80', color: '#0f0f0f' }
                  : { background: '#1a1a1a', color: '#9ca3af', border: '1px solid #2a2a2a' }}
              >
                {f === 'all' ? `All (${products.length})` : f === 'digital' ? `Digital (${digitalCount})` : `Physical (${physicalCount})`}
              </button>
            ))}
          </div>
        </div>

        {/* Linktree Section */}
        {activeLinks.length > 0 && (
          <div className="mb-8 rounded-2xl px-6 py-5" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}>
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-links-line text-sm" style={{ color: '#4ade80' }}></i>
              <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: '#6b7280' }}>Links</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {activeLinks.map(link => {
                const meta = LINK_PLATFORMS[link.platform] || LINK_PLATFORMS.custom;
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all group cursor-pointer"
                    style={{ background: '#111', border: '1px solid #2a2a2a' }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = '#4ade80')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = '#2a2a2a')}
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}>
                      <i className={`${meta.icon} text-base`} style={{ color: meta.color }}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: '#e5e7eb' }}>{link.label}</p>
                    </div>
                    <i className="ri-external-link-line text-xs flex-shrink-0" style={{ color: '#374151' }}></i>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="rounded-2xl p-20 text-center mb-12" style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}>
            <i className="ri-store-2-line text-5xl mb-4 block" style={{ color: '#374151' }}></i>
            <h3 className="text-lg font-bold text-white mb-2">{searchQuery ? 'No products found' : 'No Products Yet'}</h3>
            <p className="text-sm" style={{ color: '#6b7280' }}>
              {searchQuery ? `No products match "${searchQuery}".` : 'Check back later!'}
            </p>
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="mt-4 px-5 py-2 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap" style={{ background: '#4ade80', color: '#0f0f0f' }}>
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pb-16">
            {filteredProducts.map(product => {
              const isPhysical = product.product_type === 'physical';
              const outOfStock = !product.stock_unlimited && product.stock_quantity <= 0;
              const firstMedia = product.media_files && product.media_files.length > 0 ? product.media_files[0] : null;
              const src = firstMedia ? firstMedia.url : product.cover_image;
              const isVid = firstMedia ? firstMedia.type === 'video' : isVideoUrl(product.cover_image);
              return (
                <div
                  key={product.id}
                  onClick={() => onCardClick(product)}
                  className="rounded-2xl overflow-hidden flex flex-col cursor-pointer group transition-all duration-300 hover:scale-[1.02]"
                  style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}
                >
                  <div className="relative overflow-hidden bg-black flex-shrink-0" style={{ minHeight: '180px' }}>
                    {src ? (
                      isVid ? (
                        <video src={src} className="w-full h-auto object-contain" muted autoPlay loop playsInline />
                      ) : (
                        <img src={src} alt={product.title} className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-500" />
                      )
                    ) : (
                      <div className="w-full h-44 flex flex-col items-center justify-center" style={{ background: '#111' }}>
                        <i className={`${categoryIcons[product.category] || 'ri-file-line'} text-5xl mb-2`} style={{ color: '#374151' }}></i>
                        <span className="text-xs capitalize" style={{ color: '#6b7280' }}>{product.category}</span>
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${isPhysical ? 'bg-orange-500 text-white' : 'text-black'}`}
                        style={!isPhysical ? { background: '#4ade80' } : {}}>
                        <i className={isPhysical ? 'ri-box-3-line' : 'ri-download-cloud-line'}></i>
                        {isPhysical ? 'Physical' : 'Digital'}
                      </span>
                    </div>
                    {outOfStock && (
                      <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
                        <span className="font-bold px-4 py-2 rounded-full text-sm" style={{ background: '#1a1a1a', color: '#9ca3af' }}>Out of Stock</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="text-sm font-bold text-white line-clamp-2 mb-1">{product.title}</h3>
                    <p className="text-xs mb-3 line-clamp-2 flex-1" style={{ color: '#6b7280' }}>{product.description}</p>
                    <div className="flex items-center justify-between mt-auto pt-3" style={{ borderTop: '1px solid #2a2a2a' }}>
                      <div className="text-lg font-extrabold" style={{ color: '#4ade80' }}>
                        {product.currency} {product.price.toLocaleString()}
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); if (!outOfStock) onBuyNow(product); }}
                        disabled={outOfStock}
                        className="px-4 py-2 rounded-xl font-semibold text-xs flex items-center gap-1.5 transition-all cursor-pointer whitespace-nowrap"
                        style={outOfStock ? { background: '#2a2a2a', color: '#6b7280', cursor: 'not-allowed' } : isPhysical ? { background: '#f97316', color: '#fff' } : { background: '#4ade80', color: '#0f0f0f' }}
                      >
                        <i className={isPhysical ? 'ri-shopping-cart-line' : 'ri-download-line'}></i>
                        {isPhysical ? 'Buy Now' : 'Get Now'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="py-8 mt-4" style={{ background: '#0a0a0a', borderTop: '1px solid #1a1a1a' }}>
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm" style={{ color: '#6b7280' }}>
            <i className="ri-store-2-line" style={{ color: '#4ade80' }}></i>
            <span>Powered by <strong className="text-white">EventHub Store</strong></span>
          </div>
          <div className="flex items-center gap-4 text-xs" style={{ color: '#6b7280' }}>
            <span className="flex items-center gap-1"><i className="ri-shield-check-line" style={{ color: '#4ade80' }}></i> Secure Payments</span>
            <span className="flex items-center gap-1"><i className="ri-lock-line" style={{ color: '#4ade80' }}></i> Buyer Protected</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
